/**
 * WorkspaceLeft - Sol panel
 *
 * Icerik:
 * - Ekonomi bilgileri (butce, harcanan, FLZ ekle)
 * - Cycle progress bar
 * - Aktif uzmanlar + quality skoru
 * - Cycle gecmisi
 * - Direktif input kutusu
 */

import { Clock, DollarSign, MessageSquare, TrendingUp, Users } from 'lucide-react'
import { useState } from 'react'
import DirectiveHistory from './DirectiveHistory'
import DirectiveInput from './DirectiveInput'
import DirectivePreviewModal from './DirectivePreviewModal'

interface Project {
  id: number
  title: string
  current_cycle: number
  total_cycles_planned: number | null
  budget_flz: number
  spent_flz: number
  status: string
  assigned_experts: string[]
}

interface WorkspaceLeftProps {
  project: Project
  onUpdate: () => void
}

export default function WorkspaceLeft({ project, onUpdate }: WorkspaceLeftProps) {
  const [showDirectivePreview, setShowDirectivePreview] = useState(false)
  const [directivePreview, setDirectivePreview] = useState<any>(null)
  const [showHistory, setShowHistory] = useState(false)

  const handleDirectiveSent = () => {
    // Direktif gonderildi, preview modal goster
    // Real API response
    setDirectivePreview({
      directive_type: 'direction_change',
      effect: 'Sonraki cycle\'a eklenecek',
      next_cycle_preview: {
        cycle: project.current_cycle + 1,
        added_focus: 'Yeni odak alani',
        added_experts: ['feynman'],
        budget_change: 200
      },
      confirm_required: true
    })
    setShowDirectivePreview(true)
  }

  const handleConfirmDirective = () => {
    setShowDirectivePreview(false)
    setDirectivePreview(null)
    onUpdate()
  }

  const handleCancelDirective = () => {
    setShowDirectivePreview(false)
    setDirectivePreview(null)
  }

  const cycleProgress = project.total_cycles_planned
    ? (project.current_cycle / project.total_cycles_planned) * 100
    : 0

  const budgetUsage = project.budget_flz > 0
    ? (project.spent_flz / project.budget_flz) * 100
    : 0

  return (
    <div className="space-y-5">
      {/* Ekonomi Paneli */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2">
            <DollarSign size={20} />
            Ekonomi
          </h3>
        </div>
        <div className="card-body space-y-4">
          {/* Butce */}
          <div>
            <div className="flex justify-between mb-2">
              <span style={{ color: 'var(--text-secondary)' }}>Butce</span>
              <span style={{ color: 'var(--text-primary)' }} className="font-semibold">
                {project.budget_flz.toLocaleString()} FLZ
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-secondary)' }}>Harcanan</span>
              <span style={{ color: 'var(--text-primary)' }} className="font-semibold">
                {project.spent_flz.toLocaleString()} FLZ
              </span>
            </div>
          </div>

          {/* Progress bar */}
          <div>
            <div className="flex justify-between mb-2">
              <span style={{ color: 'var(--text-secondary)' }}>Kullanim</span>
              <span style={{ color: 'var(--text-primary)' }} className="font-semibold">
                %{budgetUsage.toFixed(0)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all"
                style={{ width: `${Math.min(budgetUsage, 100)}%` }}
              />
            </div>
          </div>

          {/* FLZ Ekle butonu */}
          <button className="btn btn-primary w-full">
            FLZ Ekle
          </button>
        </div>
      </div>

      {/* Cycle Progress */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2">
            <TrendingUp size={20} />
            Cycle {project.current_cycle}
            {project.total_cycles_planned && ` / ${project.total_cycles_planned}`}
          </h3>
        </div>
        <div className="card-body">
          {project.total_cycles_planned ? (
            <>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className="bg-success h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(cycleProgress, 100)}%` }}
                />
              </div>
              <p style={{ color: 'var(--text-secondary)' }} className="text-sm">
                %{cycleProgress.toFixed(0)} tamamlandi
              </p>
            </>
          ) : (
            <p style={{ color: 'var(--text-secondary)' }} className="text-sm">
              Arastirma projesi (sonsuz cycle)
            </p>
          )}
        </div>
      </div>

      {/* Aktif Uzmanlar */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2">
            <Users size={20} />
            Aktif Uzmanlar
          </h3>
        </div>
        <div className="card-body">
          {project.assigned_experts.length > 0 ? (
            <div className="space-y-2">
              {project.assigned_experts.map((expertId) => (
                <div
                  key={expertId}
                  className="flex items-center justify-between p-2 rounded"
                  style={{ background: 'var(--card-bg-secondary)' }}
                >
                  <span style={{ color: 'var(--text-primary)' }}>{expertId}</span>
                  <span className="badge badge-success">Aktif</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-secondary)' }} className="text-sm">
              Henuz uzman atanmadi
            </p>
          )}
        </div>
      </div>

      {/* Direktif Input */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="card-title flex items-center gap-2">
              <MessageSquare size={20} />
              Direktif Gonder
            </h3>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="btn btn-sm btn-secondary flex items-center gap-2"
            >
              <Clock size={16} />
              {showHistory ? 'Gizle' : 'Gecmis'}
            </button>
          </div>
        </div>
        <div className="card-body">
          {showHistory ? (
            <DirectiveHistory projectId={project.id} />
          ) : (
            <DirectiveInput
              projectId={project.id}
              onDirectiveSent={handleDirectiveSent}
            />
          )}
        </div>
      </div>

      {/* Direktif Preview Modal */}
      {showDirectivePreview && directivePreview && (
        <DirectivePreviewModal
          preview={directivePreview}
          onConfirm={handleConfirmDirective}
          onCancel={handleCancelDirective}
        />
      )}
    </div>
  )
}
